// What the models list cannot say about itself: whether production is scoring
// each live model, how that is going, and whether the result reaches a CRM.
//
// Follows `livePerformanceApi.ts` rather than the store pattern: one page-scoped
// read with no shared state, and `src/store/index.ts` is edited on every other
// branch in flight.
import axios from 'axios';

import { LivePerformance } from '../model/livePerformanceApi';
import { TrainingSource } from '../../../models/trainingSource';

/** One place a model's score is written. */
export interface PushDestination {
  connector: string;
  object: string;
}

export interface ModelPushStatus {
  /**
   * Whether the push config can speak for this model at all.
   *
   * False is not "not pushed". Secondary fit models are never named in the push
   * attributes, so for them nothing can be concluded, and rendering that as
   * "not pushed" would assert something no data supports.
   */
  known: boolean;
  /** Empty with `known: true` IS "not pushed": deployed, nothing enabled. */
  destinations: PushDestination[];
}

export interface ModelLiveStatus {
  performance: LivePerformance | null;
  push?: ModelPushStatus;
  /**
   * What the model was built on, already reduced to what is shown. Present for
   * drafts as well as live models, and the only field here that is.
   */
  trainedOn?: TrainingSource;
}

export type ModelsLiveStatus = Record<string, ModelLiveStatus>;

/**
 * Live status for every live model in the tenant, keyed by model id.
 *
 * An empty object on any failure, because this decorates a list that has to
 * render without it. The list is the page; these are the extra columns, and a
 * bongo outage should cost the columns rather than the page.
 */
export function fetchModelsLiveStatus(
  tenant: string | number,
  segments: string[]
): Promise<ModelsLiveStatus> {
  return axios
    .get<ModelsLiveStatus>(`/api/tenant/${tenant}/models/live_status`, {
      params: { segments: segments.join(',') },
    })
    .then(({ data }) => data ?? {})
    .catch(() => ({}));
}
